// ============================================================================
// Shared String Helpers
// Small, pure utilities for common string transforms. Keep this file lean -
// anything domain-specific (field formatters, period parsers) belongs in
// fieldFormatters.js / shared.js instead.
// ============================================================================

/**
 * Truncate a string to a maximum visible length, appending `…` when cut.
 * The returned string is at most `maxLength` characters (ellipsis included).
 *
 * @param {string|null|undefined} str
 * @param {number} maxLength - Total visible length including the ellipsis
 * @param {string} [ellipsis='...']
 * @returns {string}
 *
 * @example
 * truncateString('Hello world', 8);          // 'Hello...'
 * truncateString('Short', 10);               // 'Short'
 * truncateString(null, 5);                   // ''
 */
export function truncateString(str, maxLength, ellipsis = '...') {
    if (str == null) return '';
    const s = String(str);
    if (s.length <= maxLength) return s;
    const cut = Math.max(0, maxLength - ellipsis.length);
    return s.substring(0, cut) + ellipsis;
}

/**
 * Convert a string to kebab-case: lowercase, spaces & underscores → dashes,
 * runs collapsed, leading/trailing dashes stripped.
 *
 * @param {string|null|undefined} str
 * @returns {string}
 *
 * @example
 * toKebabCase('Hello World');        // 'hello-world'
 * toKebabCase('snake_case text');    // 'snake-case-text'
 * toKebabCase('  Trim  Me  ');       // 'trim-me'
 */
export function toKebabCase(str) {
    if (str == null) return '';
    return String(str)
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * Convert a string to snake_case: lowercase, spaces & dashes -> underscores,
 * runs collapsed, leading/trailing underscores stripped. Non-alphanumerics
 * are replaced with underscores so labels slug cleanly as variable names.
 *
 * @param {string|null|undefined} str
 * @returns {string}
 *
 * @example
 * toSnakeCase('Hello World');           // 'hello_world'
 * toSnakeCase('Number of Shareholders'); // 'number_of_shareholders'
 * toSnakeCase('  trim  me  ');          // 'trim_me'
 */
export function toSnakeCase(str) {
    if (str == null) return '';
    return String(str)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
}

/**
 * Convert a string to Title Case - first letter of each word uppercased,
 * rest lowercased. Whitespace is preserved. Does not special-case acronyms
 * or short function words (e.g. "a", "the"); use sparingly for display only.
 *
 * @param {string|null|undefined} str
 * @returns {string}
 *
 * @example
 * toTitleCase('hello world');        // 'Hello World'
 * toTitleCase('ABC DEF');            // 'Abc Def'
 * toTitleCase("o'brien");            // "O'brien"
 */
export function toTitleCase(str) {
    if (str == null) return '';
    return String(str)
        .toLowerCase()
        .replace(/\b[a-z]/g, char => char.toUpperCase());
}

/**
 * A count with its noun: `plural(1, 'journal')` -> "1 journal",
 * `plural(3, 'journal')` -> "3 journals". An irregular noun passes its own
 * plural. Pure, so the drafter's relative-import modules can reach it.
 */
export function plural(n, word, words = `${word}s`) {
    return `${n} ${n === 1 ? word : words}`;
}
